import type { ISolutionListResponse } from '@eureka/shared/solution-card/src/types';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import psHttp from '@patsnap/common-ps-http';
import {
    type PatSnapCommonResponseData,
    type PatSnapSuccessResponseData,
    PsHttpService,
} from '@patsnap/tahiti-core/common/ps-http';
import { getClientTimezone } from '@/infrastructure/utils/timezone';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { EventData, StreamData, ToolCallData, UserInputResponseData } from '../types/index';
import type { EurekaModel } from '@/domains/ai-search/config/model-select';
import { controlDeferred } from '@eureka/utils/control';

export interface AiSearchFilter {
    resource?: string[]; // 数据源
    year?: [number, number] | [string]; // 发表时间(年)
    legal_status?: string[]; // 方案成熟度: ["verified", "new", "free"]
    country?: { name: string; include: boolean }; // 国家与地区
    company?: { name: string; include: boolean }; // 竞争对手
}

export interface AiSearchJobRequest {
    initial_message?: string;
    conversation_id?: string;
    filter?: AiSearchFilter;
}

export interface AiSearchJobResponse {
    session_id: string;
    conversation_id: string;
}

export interface StreamLoopMessageRequest {
    data: EventData[];
    status: 'running' | 'finish'
}

export interface ConversationStreamLoopRequest {
    session_id: string;
    cursor?: string | null;
    last_event_id?: string | null;
    limit?: number;
    wait_ms?: number;
}

export interface ConversationStreamLoopResponse {
    events: StreamData[];
    cursor: string | null;
    stream_cursor?: string | null;
    status: 'running' | 'completed' | 'failed';
    terminal: boolean;
    has_more: boolean;
    source?: 'stream' | 'history' | 'legacy_list';
    /** 会话级业务模型名（仅首次 poll/cursor 为空时返回） */
    model_name?: string;
    /** 会话级项目选择（仅首次 poll/cursor 为空时返回），逗号分隔的 projectId 列表 */
    work_project?: string;
}

export interface FigureDocumentRequest {
    chart_type: string;
    chart_query: string;
    efq: string;
    limit: number;
    page: number;
}

export interface StatisticDocumentRequest {
    query: string;
    limit: number;
    page: number;
    solution_type: 'patent' | 'paper';
}

export interface PatsnapDocumentRequest {
    patent_ids?: string[];
    paper_ids?: string[];
    solution_type: 'patent' | 'paper';
    limit: number;
    page: number;
}

export interface DocDocumentRequest {
    session_id: string;
    limit: number;
    page: number;
    solution_type: 'patent' | 'paper';
}

export interface FigureDataResponse {
    chart_type: 'apd_y' | 'paper_datetime_ts' | 'country' | 'company_analysis' | 'word_cluster' | 'key_benefit_phrase_adc_c' | 'ancs_key_benefit_phrase' | 'apd_y_ancs';
    chart_data?: any; // For table or other types
    chart_query: string;
    [key: string]: any; // Allow additional properties
}

export interface IAISearchSolutionListResponse extends ISolutionListResponse {
    application?: string;
}

export interface AggregateDistributions {
    charts: FigureDataResponse[];
    distributions: Record<string, any>;
    paper_query: number;
    patent_query: number;
}

export interface ImageDataRequest {
    image_id: string;
    patent_id: string;
}

export interface ImageDataResponse {
    image_id: string;
    patent_id: string;
    storage_path: string;
    thumbnail_url: string;
}

/**
 * 查询请求参数
 */
export interface ConversationRequest {
    query: string;
    /** Enable the isolated parallel report-writer path. Omitted for the legacy path. */
    parallel?: boolean;
    work_project?: string;
    session_id?: string;
    context?: Record<string, any>;
    image_ids?: string[];
    file_ids?: string[];
    skill_list?: string[];
    /** Browser IANA timezone, used as the default for scheduled tasks created in chat. */
    timezone?: string;
    /** 前端指定的主对话模型业务名（如 deepseek / kimi / gpt），经请求体 model_name 字段传给后端 */
    eurekaModel?: EurekaModel;
    from?: string;
}

/**
 * 意图分析
 */
export interface IntentAnalysis {
    inferred_intent: string;
    primary_goal: string;
}

/**
 * 任务复杂度
 */
export interface ComplexityInfo {
    score: number;
    reasoning: string;
    factors: {
        tool_requirement: number;
        data_processing: number;
        intent_clarity: number;
        output_requirement: number;
    };
}
/**
 * 查询响应数据
 */
export interface QueryResponseData {
    session_id: string;
    user_query: string;
    mode: string;
    complexity?: ComplexityInfo;
    intent_analysis?: IntentAnalysis;
}

/**
 * 查询响应
 */
export interface ConversationResponse {
    session_id: string;
    query_order?: number;
    intent_type?: string;
    message: string;
    response_type: string;
    data?: QueryResponseData;
    next_expected_input?: string | null;
    requires_user_input?: boolean;
    /** 本轮会话实际使用的标准模型名；新会话为归一化结果，追问为会话创建时锁定的值 */
    model_name?: string;
    /** 创建会话时传入的项目选择，后端原值回传，供前端恢复选中态 */
    work_project?: string;
}

export interface WaitingForInputResponse extends UserInputResponseData {
    turn_id: number;
}

export interface WaitingForInputAcceptedResponse {
    session_id: string;
    turn_id: number;
    tool_id: string;
    accepted: true;
}

export interface TaskPlanResponse {
    activeForm: string;
    content: string;
    status: 'in_progress' | 'pending' | 'completed';
}

export enum InterruptSessionAction {
    PAUSE = 'pause', // 暂停执行
    RESUME = 'resume', // 恢复执行
    CANCEL = 'cancel' // 取消执行
}

export enum InterruptSessionReason {
    USER_INPUT = 'user_input', // 用户主动中断
    CLARIFICATION = 'clarification', // 需要澄清
    ADDITIONAL_INFO = 'additional_info', // 需要补充信息
    ERROR = 'error' // 错误中断
}

export interface InterruptSessionRequest {
    session_id: string;
    action: InterruptSessionAction;
    reason: InterruptSessionReason;
    metadata?: Record<string, any>
}

export interface InterruptSessionResponse {
    session_id: string;
    action: InterruptSessionAction;
    reason: InterruptSessionReason;
}

export interface ServerDataSource {
    data_type: 'eureka' | 'workspace'
    data_source: {
        source_type: ('paper' | 'news' | 'patent')[]
        patent_type: ('design' | 'invention_application' | 'utility_model')[]
        space_id: string
        folder_id: string
        field_id: string
        space_name?: string
        folder_name?: string
    }
}

export interface ChatStartRequest extends ServerDataSource {
    agent_type: string
    input: string
    chat_type: 'NEW' | 'EDIT'
}

export interface TranslateTextRequest {
    text: string;
    from: string;
    to: string;
}

export interface RenderTemplateRequest {
    tool_name: string;
    sample: Record<string, unknown>;
    lang: string;
}

export interface RenderTemplateResponse {
    template: string | null;
    cache_hit: boolean;
    tool_name: string;
}

export interface IntentRequest {
    session_id: string;
    turn_id: number;
}

export interface IntentResponse {
    session_id: string;
    turn_id: number;
    intent: 'lifescience' | 'materials' | 'general' | null;
}

export interface AiSearchApi {
    createConversation(request: ConversationRequest): Promise<ConversationResponse>;
    submitWaitingForInputResponse(
        sessionId: string,
        toolId: string,
        response: WaitingForInputResponse,
    ): Promise<WaitingForInputAcceptedResponse>;
    transConversation(session_id: string): Promise<string>;
    getConversationStream(session_id: string, cursor?: string | null): PsHttpSSE<StreamData>;
    getConversationStreamLoop(data: ConversationStreamLoopRequest): Promise<ConversationStreamLoopResponse>;
    getConversationEvents(data: ConversationStreamLoopRequest): Promise<ConversationStreamLoopResponse>;
    getAllStatisticDocsData(data: StatisticDocumentRequest): Promise<IAISearchSolutionListResponse>;
    getAllPatsnapDocsData(data: PatsnapDocumentRequest): Promise<IAISearchSolutionListResponse>;
    interruptSession(request: InterruptSessionRequest): Promise<InterruptSessionResponse>;
    getTaskPlan(session_id: string): Promise<TaskPlanResponse[]>;
    getToolResult(session_id: string, tool_id: string): Promise<ToolCallData>;
    startSession: (data: ChatStartRequest) => Promise<any>;
    translateText(data: TranslateTextRequest): Promise<{ lang: string; text: string }>;

    getRecommendCases(userInput: string, signal?: AbortSignal): Promise<{ recommend_cases: string[] }>;
    createAiSearchJob(data: AiSearchJobRequest): Promise<AiSearchJobResponse>;
    getJobStream(data: {
        conversation_id: string;
        session_id?: string;
    }): PsHttpSSE<EventData>;
    getAISearchStreamLoop(data: {
        conversation_id: string;
        session_id?: string;
    }): Promise<StreamLoopMessageRequest>;
    getFigureData(id: string, data: { chart_type: string, chart_id: string, query?: string }): Promise<FigureDataResponse>;
    getAllDocumentsData(data: DocDocumentRequest): Promise<IAISearchSolutionListResponse>;
    getDocumentsData(data: FigureDocumentRequest): Promise<ISolutionListResponse>;
    getImageHref(ifLogin: boolean, data: ImageDataRequest[]): Promise<ImageDataResponse[]>;
    getAITemplate(data: RenderTemplateRequest): Promise<RenderTemplateResponse>;
    getIntent(data: IntentRequest): Promise<IntentResponse>;
}

export const AiSearchApi = Symbol('AiSearchApi');

@injectable()
export class AiSearchApiImpl extends PsHttpService implements AiSearchApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/research`,
            docBaseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`,
            baseURLV2: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka`,
            // baseURLV2: 'http://localhost:8000/api/eureka',
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    async createConversation(request: ConversationRequest): Promise<ConversationResponse> {
        const { eurekaModel, from, ...rest } = request;
        // base / claude 等价于默认模型，不传 model_name，让后端走默认主模型
        const isDefaultModel = !eurekaModel || eurekaModel === 'base' || eurekaModel === 'claude';
        const timezone = rest.timezone || getClientTimezone();
        const body = {
            ...rest,
            timezone,
            page_source: from || await controlDeferred.promise.then((res) => res.plg_home.includes('_rd_') ? 'rd-home' : undefined),
            ...(eurekaModel && !isDefaultModel ? { model_name: eurekaModel } : {}),
        };
        return this.post<PatSnapCommonResponseData<ConversationResponse>>({
            url: `${this.options.baseURLV2}/query/conversational`,
            data: body,
        }).then((res) => {
            return res as unknown as ConversationResponse;
        });
    }

    async submitWaitingForInputResponse(
        sessionId: string,
        toolId: string,
        response: WaitingForInputResponse
    ): Promise<WaitingForInputAcceptedResponse> {
        return this.post<PatSnapCommonResponseData<WaitingForInputAcceptedResponse>>({
            url: `${this.options.baseURLV2}/sessions/${encodeURIComponent(sessionId)}/input-requests/${encodeURIComponent(toolId)}/response`,
            data: response,
        }).then(res => res as unknown as WaitingForInputAcceptedResponse);
    }

    async transConversation(session_id: string): Promise<string> {
        return this.post<PatSnapCommonResponseData<string>>({
            url: `${this.options.baseURLV2}/sessions/${session_id}/guest-update`,
        }).then((res) => {
            return res.data as string;
        });
    }

    /**
     * 获取任务的流
     */
    getConversationStream(session_id: string, cursor?: string | null) {
        const stream = this._factorySsePsHttp<StreamData>()
            .init({
                url: `${this.options.baseURLV2}/stream/${session_id}`,
                method: 'GET',
                hasFinalMessage: () => true,
            });
        if (cursor) {
            stream.setFetchHeaders({ 'Last-Event-ID': cursor });
        }
        return stream;
    }

    getConversationStreamLoop(data: ConversationStreamLoopRequest) {
        const { session_id, ...payload } = data;
        return this.post<ConversationStreamLoopResponse>({
            url: `${this.options.baseURLV2}/stream/${session_id}/poll`,
            data: payload,
        });
    }

    getConversationEvents(data: ConversationStreamLoopRequest) {
        const { session_id, ...payload } = data;
        return this.post<ConversationStreamLoopResponse>({
            url: `${this.options.baseURLV2}/sessions/${session_id}/events`,
            data: payload,
        });
    }

    /**
     * @deprecated 获取资料数据
     */
    getAllStatisticDocsData(data: StatisticDocumentRequest) {
        return this.post<PatSnapCommonResponseData<IAISearchSolutionListResponse>>({
            url: `${this.options.docBaseURL}/agent/tech-graph/statistic-documents`,
            data: {
                query: data.query,
                solution_type: data.solution_type,
                limit: data.limit,
                page: data.page,
            },
        }).then((res) => {
            return res.data as IAISearchSolutionListResponse;
        });
    }

    /**
     * 获取资料数据
     */
    getAllPatsnapDocsData(data: PatsnapDocumentRequest) {
        return this.post<PatSnapCommonResponseData<IAISearchSolutionListResponse>>({
            url: `${this.options.docBaseURL}/agent/tech-graph/statistic-documents`,
            data,
        }).then((res) => {
            return res.data as IAISearchSolutionListResponse;
        });
    }

    async interruptSession(request: InterruptSessionRequest): Promise<InterruptSessionResponse> {
        const response = await psHttp({
            url: `${this.options.baseURLV2}/interrupt`,
            method: 'POST',
            data: request,
        }).then(({ data }) => {
            return data;
        });
        return response as InterruptSessionResponse;
    }

    async getTaskPlan(session_id: string): Promise<TaskPlanResponse[]> {
        const response = await psHttp({
            url: `${this.options.baseURLV2}/admin/task-plan/${session_id}?type=task`,
            method: 'GET',
        }).then(({ data }) => {
            return data;
        });
        return response.tasks as TaskPlanResponse[];
    }

    async getToolResult(session_id: string, tool_id: string): Promise<ToolCallData> {
        const response = await psHttp({
            url: `${this.options.baseURLV2}/admin/tool-result/${session_id}/${tool_id}`,
            method: 'GET',
        }).then(({ data }) => {
            return data;
        });
        return response.content as ToolCallData;
    }

    async startSession(data: ChatStartRequest) {
        const res = await this.post<PatSnapSuccessResponseData<any>>({
            url: `${this.options.docBaseURL}/intelligence/brief/chat-start`,
            data,
        })
        return res.data
    }

    /**
     * 问题联想
     */
    getRecommendCases(userInput: string, signal?: AbortSignal) {
        return this.post<PatSnapCommonResponseData<{ recommend_cases: string[] }>>({
            url: `${this.options.docBaseURL}/plg-home-page/plg-recommend-cases`,
            data: {
                user_input: userInput,
            },
            signal,
        }).then((res) => {
            return res.data as { recommend_cases: string[] };
        });
    }

    /**
     * 创建一个AI搜索任务
     */
    createAiSearchJob(data: AiSearchJobRequest) {
        return this.post<PatSnapCommonResponseData<AiSearchJobResponse>>({
            url: `${this.options.baseURL}/sessions`,
            data,
        }).then((res) => {
            return res.data as AiSearchJobResponse;
        });
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    /**
     * 获取任务的流
     */
    getJobStream(data: {
        conversation_id: string;
        session_id?: string;
    }) {
        return this._factorySsePsHttp<EventData>()
            .init({
                url: `${this.options.baseURL}/conversations/${data.conversation_id}/stream`,
                data,
                hasFinalMessage: () => true,
            });
    }

    /**
     * sse降级轮询
     */
    getAISearchStreamLoop(data: AiSearchJobRequest) {
        return this.post<PatSnapCommonResponseData<StreamLoopMessageRequest>>({
            url: `${this.options.baseURL}/conversations/${data.conversation_id}/status`,
            data,
        }).then((res) => {
            return res.data as StreamLoopMessageRequest;
        });
    }

    /**
     * 获取图表/图片数据
     */
    getFigureData(id: string, data: { chart_type: string, chart_id: string, query?: string }) {
        return this.post<PatSnapCommonResponseData<FigureDataResponse>>({
            url: `/sessions/${id}/chart-data`,
            data,
        }).then((res) => {
            return res.data as FigureDataResponse;
        });
    }

    /**
     * 获取资料数据
     */
    getAllDocumentsData(data: DocDocumentRequest) {
        return this.post<PatSnapCommonResponseData<IAISearchSolutionListResponse>>({
            url: `/sessions/${data.session_id}/documents`,
            data: {
                session_id: data.session_id,
                solution_type: data.solution_type,
                limit: data.limit,
                page: data.page,
            },
        }).then((res) => {
            return res.data as IAISearchSolutionListResponse;
        });
    }

    /**
     * 获取图表/图片数据
     */
    getDocumentsData(data: FigureDocumentRequest) {
        return this.post<PatSnapCommonResponseData<ISolutionListResponse>>({
            url: `${this.options.docBaseURL}/agent/tech-graph/chart-documents`,
            data: {
                chart_type: data.chart_type,
                chart_query: data.chart_query,
                efq: data.efq,
                limit: data.limit,
                page: data.page,
            },
        }).then((res) => {
            return res.data as ISolutionListResponse;
        });
    }

    async translateText(data: TranslateTextRequest): Promise<{ lang: string; text: string }> {
        return this.post<PatSnapCommonResponseData<{ lang: string; text: string }>>({
            url: `${this.options.docBaseURL}/translate/text`,
            data,
        }).then((res) => {
            return res.data as { lang: string; text: string };
        });
    }

    /**
     * 获取图片
     */
    getImageHref(ifLogin: boolean, data: ImageDataRequest[]) {
        const url = ifLogin ? `${this.options.docBaseURL}/image/patent/sign` : `${this.options.docBaseURL}/guest/patent-img/sign`
        return this.post<PatSnapCommonResponseData<ImageDataResponse[]>>({
            url,
            data,
        }).then((res) => {
            return res.data as ImageDataResponse[];
        });
    }

    getAITemplate(data: RenderTemplateRequest): Promise<RenderTemplateResponse> {
        return this.post<RenderTemplateResponse>({
            url: `${this.options.baseURLV2}/render-template`,
            // url: 'http://localhost:8000/api/eureka/render-template',
            data,
        });
    }

    getIntent(data: IntentRequest): Promise<IntentResponse> {
        return this.post<PatSnapCommonResponseData<IntentResponse>>({
            url: `${this.options.baseURLV2}/intent`,
            data,
        }).then((res) => {
            return res.data as IntentResponse;
        });
    }
}
